import { useEffect } from "react";
import { useAsyncFn } from "react-use";

type GraphQLError = {
  message: string;
};

export async function apiQuery<ReturnType, InputType = unknown>(
  query: string,
  variables?: InputType,
  overrideUrl?: string,
): Promise<{ data: ReturnType | null; error: boolean }> {
  try {
    const response = await fetch(
      overrideUrl ?? import.meta.env.VITE_GAME_SERVER_API,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query,
          variables,
        }),
      },
    );

    if (!response.ok) {
      // Handle HTTP errors (like 4xx or 5xx responses)
      console.error(`HTTP error! status: ${response.status}`);
      return { data: null, error: true };
    }

    const { data, errors } = (await response.json()) as {
      data: ReturnType;
      errors?: GraphQLError[];
    };

    if (errors) {
      throw new Error(errors.map((error) => error.message).join("\n"));
    }

    return { data, error: false };
  } catch (error) {
    // Catch network errors or unexpected issues
    console.error("Error occurred during API query:", error);
    return { data: null, error: true };
  }
}

export default function useQuery<T, InputType = never>({
  query,
  variables,
}: {
  query: string;
  variables?: InputType;
}) {
  const [{ loading, value: data }, fetch] = useAsyncFn(async () => {
    const response = await apiQuery<T, InputType>(query, variables);
    return response.data;
  }, [query, variables]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { loading, data, refetch: fetch };
}